import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { get } from "@/lib/api";
import { Timestamp } from "@/components/common";

export function useLiveStatus() {
  return useQuery({ queryKey: ["live-status"], queryFn: () => get("/live/status"), refetchInterval: 10000 });
}

// Mounted once in Layout: toasts when the live feed starts (or resumes) delivering data.
export function FeedWatcher() {
  const { data } = useLiveStatus();
  const qc = useQueryClient();
  const seen = useRef({ init: false, recovered: null, receiving: null, provider: null });
  useEffect(() => {
    if (!data) return;
    const s = seen.current;
    if (!s.init || s.provider !== data.provider) {
      s.init = true;
      s.provider = data.provider;
      s.recovered = data.recovered_at;
      s.receiving = data.receiving;
      return;
    }
    if (data.recovered_at && data.recovered_at !== s.recovered) {
      toast.success(`Live AIS (${data.provider_label}) is receiving data`, { description: `First message at ${data.recovered_at} · ${data.vessels_recent_10min} vessel(s) in the last ${data.recent_window_minutes} min`, duration: 10000 });
      qc.invalidateQueries({ queryKey: ["live-vessels"] });
      qc.invalidateQueries({ queryKey: ["live-coverage"] });
    } else if (s.receiving && !data.receiving) {
      toast.warning("Live AIS feed went silent", { description: `No new data from ${data.provider_label} — AIS unavailable, not an empty harbour.`, duration: 8000 });
    }
    s.recovered = data.recovered_at;
    s.receiving = data.receiving;
  }, [data, qc]);
  return null;
}

export function FeedNote() {
  const { data: s } = useLiveStatus();
  if (!s) return null;
  const tone = s.receiving ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : s.configured ? "border-amber-500/30 bg-amber-500/10 text-amber-200" : "border-slate-600/40 bg-slate-800/40 text-slate-300";
  return (
    <div data-testid="feed-note" className={`flex flex-wrap items-center justify-between gap-3 rounded border px-4 py-2 text-sm ${tone}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-mono text-[10px] uppercase tracking-wider opacity-80">Live AIS</span>
        {s.receiving ? <span data-testid="feed-note-receiving">Receiving data · {s.vessels_recent_10min} vessel(s) in the last {s.recent_window_minutes} min · last message <Timestamp ts={s.last_message_at} withDate={false} className="text-inherit" /></span>
          : !s.configured ? <span>Not configured — add the {s.provider_label} key to start.</span>
          : s.first_message_at ? <span data-testid="feed-note-silent">Silent since <Timestamp ts={s.last_message_at} withDate={false} className="text-inherit" /> — AIS unavailable, not an empty harbour.</span>
          : <span data-testid="feed-note-waiting">Waiting for the first vessels from {s.provider_label}{s.last_error ? ` — ${s.last_error}` : ""}.</span>}
        {s.recovered_at && <span className="font-mono text-[10px] opacity-70">· first data <Timestamp ts={s.recovered_at} withDate={false} className="text-inherit text-[10px]" /></span>}
      </div>
      <Link to="/map" className="text-xs underline-offset-2 hover:underline">Live map →</Link>
    </div>
  );
}
